/* eslint-disable sonarjs/no-duplicate-string */
import React, { useState } from 'react';
import {
  Grid,
  RACButton,
  RACSelect,
  RACTextField,
  Typography,
  Card,
  CardContent,
  RACDatePicker,
} from '@rentacenter/racstrap';
import moment from 'moment';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import SomethingWentWrongPopUp from '../Agreement/SomethingWentWrong';
import { GlobalFacturaInputStyles } from '../../JsStyles/GlobalFacturaInputStyles';

import { dropdown } from '../../../src/types/interface';
interface GlobalFacturaInputProps {
  storeDropdown?: dropdown[];
  storeLoading?: boolean;
  loading?: boolean;
  onSearch?: (filters: any) => void;
  onClear?: () => void;
  title?: string;
}

export const GlobalFacturaInput: React.FC<GlobalFacturaInputProps> = ({
  storeDropdown = [],
  storeLoading = false,
  loading = false,
  onSearch,
  onClear,
  title,
}) => {
  const { t } = useTranslation('translation');
  const classes = GlobalFacturaInputStyles();
  // const { store } = useContext(AppConfigurationContext);

  const [formData, setFormData] = useState({
    store: '',
    startDate: '',
    endDate: '',
    receiptId: '',
  });
  const [showFilters, setShowFilters] = useState(true);
  const [showSomethingWentWrong, setShowSomethingWentWrong] = useState(false);

  const handleStartDateChange = (e: any) => {
    const selectedDate = e;
    setFormData((prev) => ({
      ...prev,
      startDate: selectedDate,
      endDate: '', // Reset end date when start date changes
    }));
  };

  const handleEndDateChange = (e: any) => {
    setFormData((prev) => ({ ...prev, endDate: e }));
  };

  const handleReceiptIdChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue.length <= 15) {
      setFormData((prev) => ({ ...prev, receiptId: numericValue }));
    }
  };

  const handleReceiptIdKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isApplyEnabled) {
      handleSearch();
      return;
    }
    if (
      !/[0-9]/.test(e.key) &&
      !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
        e.key
      ) &&
      !(e.ctrlKey || e.metaKey) // Allow Ctrl/Cmd combinations for copy/paste
    ) {
      e.preventDefault();
    }
  };

  const isApplyEnabled =
    formData.store !== '' ||
    formData.receiptId.length >= 5 ||
    (formData.startDate && formData.endDate);

  const handleSearch = async () => {
    try {
      if (formData.receiptId.length >= 5 || formData.store) {
        // Transform the data to match API expectations
        const apiPayload = {
          ...(formData.store && { storeNumber: formData.store }),
          ...(formData.startDate && { startDate: formData.startDate }),
          ...(formData.endDate && { endDate: formData.endDate }),
          ...(formData.receiptId && { receiptId: formData.receiptId }), // API expects string
        };
        onSearch?.(apiPayload);
      }
    } catch (error) {
      setShowSomethingWentWrong(true);
    }
  };

  const handleClear = () => {
    setFormData({ store: '', startDate: '', endDate: '', receiptId: '' });
    onClear?.();
  };

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12} className={classes.headerGrid}>
        <Typography variant="h5" className={classes.title}>
          {title || t('Global Factura')}
        </Typography>
        <RACButton
          variant="contained"
          onClick={() => {
            setShowFilters(!showFilters);
          }}
          className={classes.filterButton}
          startIcon={<FontAwesomeIcon icon={faSlidersH} />}
        >
          {t('Filters')}
        </RACButton>
      </Grid>

      {showFilters && (
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent>
              <Grid container spacing={2} className={classes.formContainer}>
                <Grid item xs={12} md={2} className={classes.storeGrid}>
                  <label className={classes.label}>{t('Store')}</label>
                  <Grid className={classes.storeWrapper}>
                    <RACSelect
                      options={storeDropdown}
                      loading={storeLoading}
                      defaultValue={formData.store}
                      onChange={(e: any) => {
                        setFormData((prev) => ({
                          ...prev,
                          store: e.target.value,
                        }));
                      }}
                      inputLabelClassName=""
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} md={2} className={classes.fieldGrid}>
                  <label className={classes.label}>{t('Start Date')}</label>
                  <Grid className={classes.fieldWrapper}>
                    <RACDatePicker
                      label={''}
                      id="startDate"
                      value={formData.startDate}
                      name="startDate"
                      onChange={handleStartDateChange}
                      disabled={
                        !formData.store && formData.receiptId.length === 0
                      }
                      inputProps={{
                        min: moment()
                          .subtract(1, 'month')
                          .startOf('month')
                          .format('YYYY-MM-DD'),
                        max: moment().format('YYYY-MM-DD'),
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} md={2} className={classes.fieldGrid}>
                  <label className={classes.label}>{t('End Date')}</label>
                  <Grid className={classes.fieldWrapper}>
                    <RACDatePicker
                      label={''}
                      id="endDate"
                      value={formData.endDate}
                      name="endDate"
                      disabled={
                        !formData.startDate ||
                        (!formData.store && formData.receiptId.length === 0)
                      }
                      onChange={handleEndDateChange}
                      inputProps={{
                        min:
                          formData.startDate || moment().format('YYYY-MM-DD'),
                        max: moment().format('YYYY-MM-DD'),
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} md={3} className={classes.fieldGrid}>
                  <label className={classes.label}>{t('Receipt ID')}</label>
                  <Grid className={classes.fieldWrapper}>
                    <RACTextField
                      type={'text'}
                      value={formData.receiptId}
                      onChange={handleReceiptIdChange}
                      onKeyDown={handleReceiptIdKeyPress}
                      inputProps={{
                        maxLength: 15,
                        pattern: '[0-9]*',
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container className={classes.buttonContainer}>
                <Grid item xs={12} className={classes.buttonGrid}>
                  <RACButton
                    variant="outlined"
                    color="primary"
                    onClick={handleClear}
                    disabled={
                      !formData.store &&
                      !formData.startDate &&
                      !formData.endDate &&
                      !formData.receiptId
                    }
                    className={classes.button}
                  >
                    {t('Clear')}
                  </RACButton>
                  <RACButton
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={!isApplyEnabled || loading}
                    className={classes.button}
                  >
                    {t('Apply')}
                  </RACButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      <SomethingWentWrongPopUp
        tab={showSomethingWentWrong}
        setFunction={setShowSomethingWentWrong}
      />
    </Grid>
  );
};
